import { useState, useEffect } from 'react';
import {
  FaSearch,
  FaUserShield,
  FaUserTimes,
  FaUserLock,
  FaUserCheck,
  FaEdit,
  FaEye,
} from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/admin/UsersManagement.css';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    role: '',
    status: '',
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/admin/users', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        // Error will be handled by the global error handler
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleViewUser = user => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleEditUser = user => {
    setSelectedUser(user);
    setEditFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      status: user.status,
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = e => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateUser = async e => {
    e.preventDefault();

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const updatedUser = await response.json();

      // Update local state
      setUsers(users.map(user => (user.id === selectedUser.id ? updatedUser : user)));

      setShowEditModal(false);
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      // Update local state
      setUsers(users.map(user => (user.id === userId ? { ...user, status: newStatus } : user)));
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';

      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      // Update local state
      setUsers(users.map(user => (user.id === userId ? { ...user, role: newRole } : user)));
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  // Filter and search users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = filterRole === 'all' || user.role === filterRole;

    return matchesSearch && matchesRole;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className='pagination'>
        <button
          className='pagination-btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(1)}
        >
          First
        </button>
        <button
          className='pagination-btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>
        <span className='page-info'>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className='pagination-btn'
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
        <button
          className='pagination-btn'
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div className='users-management'>
      {showUserDetails && selectedUser && (
        <div className='user-details-modal'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2>User Profile</h2>
              <button className='close-btn' onClick={() => setShowUserDetails(false)}>
                &times;
              </button>
            </div>
            <div className='modal-body'>
              <div className='user-profile'>
                <div className='user-avatar'>
                  {selectedUser.avatar_url ? (
                    <img src={selectedUser.avatar_url} alt={selectedUser.username} />
                  ) : (
                    <div className='avatar-placeholder'>
                      {selectedUser.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className='user-details'>
                  <h3>{selectedUser.username}</h3>
                  <p className='user-email'>{selectedUser.email}</p>
                  <div className='user-badges'>
                    <span className={`role-badge ${selectedUser.role}`}>
                      {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                    </span>
                    <span className={`status-badge ${selectedUser.status}`}>
                      {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className='user-info-sections'>
                <div className='info-section'>
                  <h3>Account Information</h3>
                  <p>
                    <strong>Member Since:</strong>{' '}
                    {new Date(selectedUser.created_at).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Last Login:</strong>{' '}
                    {selectedUser.last_login
                      ? new Date(selectedUser.last_login).toLocaleString()
                      : 'Never'}
                  </p>
                  <p>
                    <strong>Orders:</strong> {selectedUser.order_count || 0}
                  </p>
                </div>

                <div className='info-section'>
                  <h3>Shipping Address</h3>
                  {selectedUser.shipping_address ? (
                    <>
                      <p>{selectedUser.shipping_address.street}</p>
                      <p>
                        {selectedUser.shipping_address.city}, {selectedUser.shipping_address.state}{' '}
                        {selectedUser.shipping_address.zip}
                      </p>
                      <p>{selectedUser.shipping_address.country}</p>
                    </>
                  ) : (
                    <p>No shipping address on file</p>
                  )}
                </div>
              </div>

              <div className='user-actions'>
                <button
                  className='btn-edit'
                  onClick={() => {
                    setShowUserDetails(false);
                    handleEditUser(selectedUser);
                  }}
                >
                  <FaEdit /> Edit User
                </button>

                <button
                  className={`btn-status ${selectedUser.status === 'active' ? 'deactivate' : 'activate'}`}
                  onClick={() => handleToggleStatus(selectedUser.id, selectedUser.status)}
                >
                  {selectedUser.status === 'active' ? (
                    <>
                      <FaUserTimes /> Deactivate User
                    </>
                  ) : (
                    <>
                      <FaUserCheck /> Activate User
                    </>
                  )}
                </button>

                <button
                  className={`btn-role ${selectedUser.role === 'admin' ? 'remove-admin' : 'make-admin'}`}
                  onClick={() => handleToggleRole(selectedUser.id, selectedUser.role)}
                >
                  {selectedUser.role === 'admin' ? (
                    <>
                      <FaUserLock /> Remove Admin Rights
                    </>
                  ) : (
                    <>
                      <FaUserShield /> Grant Admin Rights
                    </>
                  )}
                </button>
              </div>

              <div className='recent-activity'>
                <h3>Recent Activity</h3>
                {selectedUser.recent_activity && selectedUser.recent_activity.length > 0 ? (
                  <ul className='activity-list'>
                    {selectedUser.recent_activity.map(activity => (
                      <li
                        key={`${activity.timestamp}-${activity.description}`}
                        className='activity-item'
                      >
                        <span className='activity-date'>
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                        <span className='activity-description'>{activity.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No recent activity</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className='edit-user-modal'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2>Edit User</h2>
              <button className='close-btn' onClick={() => setShowEditModal(false)}>
                &times;
              </button>
            </div>
            <div className='modal-body'>
              <form onSubmit={handleUpdateUser}>
                <div className='form-group'>
                  <label htmlFor='username'>Username</label>
                  <input
                    type='text'
                    id='username'
                    name='username'
                    value={editFormData.username}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>

                <div className='form-group'>
                  <label htmlFor='email'>Email</label>
                  <input
                    type='email'
                    id='email'
                    name='email'
                    value={editFormData.email}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>

                <div className='form-group'>
                  <label htmlFor='role'>Role</label>
                  <select
                    id='role'
                    name='role'
                    value={editFormData.role}
                    onChange={handleEditFormChange}
                  >
                    <option value='user'>User</option>
                    <option value='admin'>Admin</option>
                  </select>
                </div>

                <div className='form-group'>
                  <label htmlFor='status'>Status</label>
                  <select
                    id='status'
                    name='status'
                    value={editFormData.status}
                    onChange={handleEditFormChange}
                  >
                    <option value='active'>Active</option>
                    <option value='inactive'>Inactive</option>
                  </select>
                </div>

                <div className='form-actions'>
                  <button
                    type='button'
                    className='btn-cancel'
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button type='submit' className='btn-save'>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className='filters-bar'>
        <div className='search-box'>
          <FaSearch className='search-icon' />
          <input
            type='text'
            placeholder='Search users by name or email...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className='role-filter'>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value='all'>All Roles</option>
            <option value='user'>Users Only</option>
            <option value='admin'>Admins Only</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {filteredUsers.length === 0 ? (
            <div className='no-users'>
              <p>No users found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className='users-table-container'>
                <table className='users-table'>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Date Joined</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map(user => (
                      <tr
                        key={user.id}
                        className={user.status === 'inactive' ? 'inactive-user' : ''}
                      >
                        <td className='username-cell'>
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt='' className='user-avatar-small' />
                          ) : (
                            <div className='avatar-placeholder-small'>
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {user.username}
                        </td>
                        <td>{user.email}</td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${user.status}`}>
                            {user.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className='table-actions'>
                            <button
                              className='btn-view'
                              onClick={() => handleViewUser(user)}
                              title='View Details'
                            >
                              <FaEye />
                            </button>

                            <button
                              className='btn-edit'
                              onClick={() => handleEditUser(user)}
                              title='Edit User'
                            >
                              <FaEdit />
                            </button>

                            <button
                              className={`btn-status-toggle ${user.status === 'active' ? 'deactivate' : 'activate'}`}
                              onClick={() => handleToggleStatus(user.id, user.status)}
                              title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                            >
                              {user.status === 'active' ? <FaUserTimes /> : <FaUserCheck />}
                            </button>

                            <button
                              className={`btn-role-toggle ${user.role === 'admin' ? 'remove-admin' : 'make-admin'}`}
                              onClick={() => handleToggleRole(user.id, user.role)}
                              title={
                                user.role === 'admin' ? 'Remove Admin Rights' : 'Grant Admin Rights'
                              }
                            >
                              {user.role === 'admin' ? <FaUserLock /> : <FaUserShield />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UsersManagement;
